<template>
  <div ref="headerMenu">
    <HeaderMenuButton :is-menu-open="isMenuOpen" @click="toggleMenu()" />

    <div class="dropdown-menu">
      <transition name="dropdownSlide">
        <div v-show="isMenuOpen" class="dropdown-menu__list">
          <SessionSelect v-if="currentCompanyLoaded" class="company-selector" />
          <HeaderMenuItemAbout />
          <shared-device-toggle />
          <button
            v-for="item in menuItems"
            :key="item.index"
            class="dropdown-menu__list-button"
            @click="item.method"
          >
            <img
              class="dropdown-menu__list-button-image"
              :src="item.icon"
              :alt="item.alt"
            />
            <div class="dropdown-menu__list-button-text">
              {{ $t(item.name) }}
            </div>
          </button>
          <HeaderMenuLocales />

          <img
            :src="attensiLogoBlack"
            class="dropdown-menu__list-attensi-logo"
            alt=""
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import {
  attensiLogoBlack,
  editMeIcon,
  logOutIcon,
  portalLogoIcon,
} from '@/assets/img'
import SessionSelect from '@/components/generics/SessionSelect.vue'
import HeaderMenuButton from '@/components/layout/header/HeaderMenuButton.vue'
import HeaderMenuItemAbout from '@/components/layout/header/HeaderMenuItemAbout.vue'
import HeaderMenuLocales from '@/components/layout/header/HeaderMenuLocales.vue'
import { useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useProductFilters } from '@/hooks/useProductFilters'
import { ROUTE_USER_EDIT } from '@/router/routes/routeNames'
import { redirectToLogout, redirectToPortal } from '@/service/loginActions'
import Detect from '@attensi/detect'
import { mapState } from 'vuex'

const MENU_ITEM_NAMES = {
  PORTAL: 'PORTAL',
  EDIT_ME: 'mainMenu.EDIT_ME',
  LOGOUT: 'mainMenu.LOGOUT',
}

export default {
  components: {
    HeaderMenuButton,
    HeaderMenuLocales,
    SessionSelect,
    HeaderMenuItemAbout,
  },
  setup() {
    const { deleteLocalStorageProductFilters } = useProductFilters()
    const { isEnabled: canUseSharedDevice } = useFeatureFlag(
      'can_use_shared_device'
    )
    const { isEnabled: mustUseSharedDevice } = useFeatureFlag(
      'must_use_shared_device'
    )
    return {
      deleteLocalStorageProductFilters,
      canUseSharedDevice,
      mustUseSharedDevice,
    }
  },
  data() {
    return {
      attensiLogoBlack,
      isMenuOpen: false,
      menuItems: [
        {
          name: MENU_ITEM_NAMES.PORTAL,
          icon: portalLogoIcon,
          alt: 'Attensi Engage',
          method: async () => {
            if (Detect.isPortalApp && Detect.isAndroid) {
              await redirectToPortal({
                open_in_new_tab: false,
              })
            } else {
              await redirectToPortal({
                open_in_new_tab: false,
              })
            }
          },
        },
        {
          name: MENU_ITEM_NAMES.EDIT_ME,
          icon: editMeIcon,
          alt: 'edit me icon',
          method: () => {
            this.goToRoute({
              name: ROUTE_USER_EDIT,
              params: { userId: this.currentUser.id },
            })
          },
        },
        {
          name: MENU_ITEM_NAMES.LOGOUT,
          icon: logOutIcon,
          alt: 'Log out icon',
          method: async () => {
            this.deleteLocalStorageProductFilters()
            await redirectToLogout()
          },
        },
      ],
    }
  },
  computed: {
    ...mapState({
      currentCompanyLoaded: (state) => state.common.currentCompanyLoaded,
      currentCompany: (state) => state.common.currentCompany,
      currentUser: (state) => state.common.currentUser,
      isAndroid: (state) => state.common.isAndroid,
    }),
  },
  unmounted() {
    document.removeEventListener('click', this.documentClick)
  },
  created() {
    document.addEventListener('click', this.documentClick)
    this.setMenuItems()
  },
  methods: {
    setMenuItems() {
      const currentCompany = this.currentCompany
      this.menuItems = this.menuItems.filter((menuItem) => {
        if (menuItem.name === MENU_ITEM_NAMES.PORTAL) {
          return currentCompany.supported_configurations.includes('portal')
        }
        return true
      })
    },
    goToRoute(path) {
      this.$router.push(path)
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    /**
     * When the menu is open, it is possible to click anywhere outside the menu to close it.
     */
    documentClick(e) {
      const dropdown = this.$refs.headerMenu
      const target = e.target

      if (dropdown !== target && !dropdown.contains(target)) {
        this.isMenuOpen = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.dropdown-menu {
  position: relative;

  &__list {
    position: absolute;
    top: -1px;
    right: 0px;
    display: flex;
    flex-direction: column;
    gap: $spacing-3xs;
    width: 100%;
    padding: $spacing-3xs;
    border: 1px solid $color-border;
    border-radius: 0 0 $border-radius-l $border-radius-l;
    background: $color-canvas;
    box-shadow: 0px 30px 40px -10px #dde2e4;
  }

  &__list-button {
    display: flex;
    align-items: center;
    border: solid 1px $color-border;
    background: $color-canvas;
    width: 100%;
    gap: $spacing-xs;
    padding: $spacing-2xs $spacing-s;
    border-radius: $border-radius-l;
    cursor: pointer;

    &:hover {
      background: $color-pebble;
    }
  }

  &__list-button-image {
    width: $spacing-xs;
    height: $spacing-xs;
    aspect-ratio: 1;
    object-fit: contain;
  }

  &__list-button-text {
    color: $color-text;
  }

  &__list-attensi-logo {
    width: 80px;
    margin: $spacing-5xs auto;
    object-fit: contain;
  }
}

/* Animation */
.dropdownSlide-enter-active,
.dropdownSlide-leave-active {
  transition: all 200ms ease-in-out;
}
.dropdownSlide-enter,
.dropdownSlide-leave-to {
  transform: translateY(-100px);
  opacity: 0;
}

/* Media queries */
@media screen and (max-width: $tablet-width-max) {
  .dropdown-menu__list {
    top: $spacing-2xs;
    border-radius: $border-radius-l;
  }

  .dropdownSlide-enter-active,
  .dropdownSlide-leave-active {
    transition: none;
    overflow: hidden;
  }
  .dropdownSlide-enter,
  .dropdownSlide-leave-to {
    transform: none;
    overflow: hidden;
  }
}

@media screen and (max-width: 767px) {
  // Reset the position, allowing the menu to cover the page:
  .dropdown-menu {
    position: static;

    &__list {
      top: unset;
      left: 0px;
      box-shadow: 0px 30px 40px -10px #7d8284;
      border-radius: 0 0 $border-radius-l $border-radius-l;
    }
  }
}
</style>
